/**
 * Tenant-wide delivery-ETA display helper (iter134q).
 *
 * Some tenants ship pan-India where the honest ETA is "2 Days" or
 * "3-5 business days", not the hardcoded "25 mins" the storefront
 * ships with. The tenant admin controls this via two fields on the
 * tenant doc (surfaced through ``useTenant()`` — see the customer
 * routes wrapped by ``TenantProvider``):
 *
 *   - ``delivery_eta_display_enabled`` (bool, default ``true``) —
 *     when false, the entire ETA pill disappears from restaurant
 *     cards, restaurant detail and the order-track status card.
 *   - ``delivery_eta_display_text`` (string, default ``""``) — when
 *     non-empty, this literal string replaces the per-shop
 *     "N mins" everywhere. Empty text = default behaviour.
 *
 * Legacy tenants (missing both fields) MUST continue to see the
 * per-shop minutes value — a plain read of an undefined field on
 * an old tenant doc returns ``undefined``, and both defaults below
 * are picked to preserve that path exactly.
 *
 * @param {object|null|undefined} tenant  — tenant doc from useTenant()
 * @param {number|null|undefined} defaultMinutes
 *   — the per-shop / per-order minutes value the caller would render
 *     if no tenant override existed (e.g. ``r.avg_prep_minutes``).
 * @returns {string|null} — the string to render inside the pill, or
 *   ``null`` when the caller should hide the pill entirely.
 */
// Same constants/formula as backend/pricing.py:eta_minutes_for — kept in
// sync deliberately so a shop's live browse-time ETA (computed here, from
// the customer's current resolved address) and the snapshot stored on an
// actual order (computed server-side at checkout) never disagree.
const AVG_DELIVERY_SPEED_KMH = 20;
const DEFAULT_PREP_MINUTES = 25;   // mirrors Restaurant.avg_prep_minutes default
const MIN_ETA_MINUTES = 15;

/**
 * Estimated delivery time = prep time + distance-based travel time.
 *
 * THE bug this fixes: every shop's ETA pill used to render JUST
 * ``avg_prep_minutes`` (default 25 on any shop that never customised
 * it) — two shops at 0 km and 4+ km away both showed "25 mins" because
 * ``distance_km`` (already computed server-side and displayed right next
 * to the pill as "X km away") was never fed into the ETA math at all.
 *
 * @param {number|null|undefined} prepMinutes — e.g. ``r.avg_prep_minutes``
 * @param {number|null|undefined} distanceKm  — e.g. ``r.distance_km``
 * @returns {number} minutes, rounded up to the nearest 5, floored at
 *   ``MIN_ETA_MINUTES``. Falls back to prep-time-only (the original
 *   behaviour) when distance isn't known yet — no customer address
 *   selected, or the shop has no coordinates.
 */
export function computeEtaMinutes(prepMinutes, distanceKm) {
    const prep = Number(prepMinutes) > 0 ? Number(prepMinutes) : DEFAULT_PREP_MINUTES;
    const dist = Number(distanceKm);
    if (!Number.isFinite(dist) || dist <= 0) {
        return Math.max(MIN_ETA_MINUTES, Math.ceil(prep / 5) * 5);
    }
    const travel = (dist / AVG_DELIVERY_SPEED_KMH) * 60;
    return Math.max(MIN_ETA_MINUTES, Math.ceil((prep + travel) / 5) * 5);
}

export function formatDeliveryEta(tenant, defaultMinutes) {
    // Missing tenant → behave exactly as legacy (show the minutes).
    // This branch runs on the first React frame before TenantProvider
    // resolves; without it the ETA pill would flicker to "" and jump.
    const enabled = tenant?.delivery_eta_display_enabled !== false;
    if (!enabled) return null;

    const custom = (tenant?.delivery_eta_display_text || "").trim();
    if (custom) return custom;

    const n = Number(defaultMinutes);
    if (Number.isFinite(n) && n > 0) return `${n} mins`;
    return null;
}

/**
 * Curated presets shown in the admin dropdown. The tenant can also
 * type any custom value (60-char cap enforced by the backend).
 */
export const DELIVERY_ETA_PRESETS = [
    { label: "Default (per-shop minutes)", value: "" },
    { label: "30 Minutes", value: "30 Minutes" },
    { label: "1 Hour", value: "1 Hour" },
    { label: "2 Hours", value: "2 Hours" },
    { label: "Same Day", value: "Same Day" },
    { label: "1 Day", value: "1 Day" },
    { label: "2 Days", value: "2 Days" },
    { label: "3 Days", value: "3 Days" },
    { label: "3-5 Days", value: "3-5 Days" },
    { label: "1 Week", value: "1 Week" },
];
